import React, { useMemo } from "react";
import type { RenderContainerNode as RenderContainerNodeModel } from "../../../util/src/layout-compiler/types.js";
import type { RenderNode } from "../../../util/src/layout-compiler/types.js";
import {
  CompiledLayoutFillContext,
  useCompiledLayoutFill,
} from "./CompiledLayoutFillContext.js";
import { nodeStyleWithoutDimensions } from "./nodeStyleHelpers.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { getSiblingRenderReactKey } from "./renderReactKey.js";
import { RenderNodeView } from "./RenderNodeView.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderContainerNodeProps = {
  node: RenderContainerNodeModel;
  scale: number;
  isSceneRoot?: boolean;
};

function getChildPositionMode(child: RenderNode): "relative" | "absolute" {
  const position = child.style?.position;
  if (typeof position === "string" && position.toLowerCase() === "relative") {
    return "relative";
  }
  return "absolute";
}

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) {
    return `${rotationTransform} ${extra}`;
  }

  return rotationTransform ?? extra;
}

export function RenderContainerNode({
  node,
  scale,
  isSceneRoot = false,
}: RenderContainerNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const fillContext = useCompiledLayoutFill();
  const isFlex = node.layout === "flex";

  const sortedChildren = useMemo(
    () =>
      isFlex
        ? [...node.children].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0))
        : node.children,
    [isFlex, node.children],
  );

  const hasRelativeChildren = sortedChildren.some(
    (child) => getChildPositionMode(child) === "relative",
  );

  const isWidthFitContent =
    isFlex && node.flexWidthMode === "fitContent" && hasRelativeChildren;
  const isHeightFitContent =
    isFlex && node.flexHeightMode === "fitContent" && hasRelativeChildren;

  const layoutStyle = nodeStyleWithoutDimensions(node.style);

  const clipStyle = getRenderNodeClipStyle(node, scale);

  const fillParent = fillContext?.fillParent === true;
  const fillSceneRoot = fillParent && isSceneRoot;

  const childFillContext = useMemo(
    () =>
      fillParent
        ? {
            fillParent: true,
            designWidth: node.width,
            designHeight: node.height,
          }
        : null,
    [fillParent, node.height, node.width],
  );

  const containerStyle: React.CSSProperties = {
    ...(fillSceneRoot
      ? {
          position: "absolute",
          left: 0,
          top: 0,
        }
      : node.style.position === "relative"
        ? {
            position: "relative",
            flexShrink: 0,
          }
        : {
            position: "absolute",
            left: node.x * scale,
            top: node.y * scale,
          }),
    zIndex: node.zIndex,
    boxSizing: "border-box",
    overflow: isFlex ? "hidden" : undefined,
    ...layoutStyle,
    width: fillSceneRoot
      ? "100%"
      : isWidthFitContent
        ? "fit-content"
        : node.width * scale,
    height: fillSceneRoot
      ? "100%"
      : isHeightFitContent
        ? "fit-content"
        : node.height * scale,
    transform: composeTransform(node.rotation, node.style?.transform),
    transformOrigin: "center center",
    ...clipStyle,
    ...(isFlex
      ? {
          display: "flex",
          flexDirection: node.flexDirection ?? "column",
          gap: node.gap != null ? `${node.gap * scale}px` : undefined,
          justifyContent: node.justifyContent ?? "flex-start",
          alignItems: node.alignItems ?? "center",
        }
      : {
          pointerEvents: node.clickable ? "auto" : "none",
        }),
  };

  if (!isFlex) {
    return (
      <>
        <div {...runtimeProps} style={containerStyle} />
        <CompiledLayoutFillContext.Provider value={childFillContext}>
          {sortedChildren.map((child, index) => (
            <RenderNodeView
              key={getSiblingRenderReactKey(child, index, sortedChildren)}
              node={child}
              scale={scale}
            />
          ))}
        </CompiledLayoutFillContext.Provider>
      </>
    );
  }

  return (
    <div
      {...runtimeProps}
      data-layout-flow={isHeightFitContent ? "content" : "fixed"}
      style={containerStyle}
    >
      <CompiledLayoutFillContext.Provider value={childFillContext}>
        {sortedChildren.map((child, index) => (
          <RenderNodeView
            key={getSiblingRenderReactKey(child, index, sortedChildren)}
            node={child}
            scale={scale}
          />
        ))}
      </CompiledLayoutFillContext.Provider>
    </div>
  );
}
